import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import {
  createCaseStudies,
  getCaseStudiesByID,
  CaseStudiesDetailsImageUpload,
  updateCaseStudies,
} from "./services/casestudies.services";
import { clearCaseStudiesDetails } from "./casestudiesSlice";
import { slugify } from "../../_helper/helper";

const CreateCaseStudies: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const CaseStudiesID = location.state?.ID;
  const CaseStudiesDetail = useAppSelector(
    (state) => state.caseStudies.CaseStudiesDetail
  );
  const loading = useAppSelector((state) => state.caseStudies.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (CaseStudiesID) {
      dispatch(getCaseStudiesByID(CaseStudiesID));
    }
    return () => {
      dispatch(clearCaseStudiesDetails());
    };
  }, [CaseStudiesID]);

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CaseStudiesDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      Description: values.Description ?? "",
      CaseStudieDetail: values.CaseStudieDetail ?? "",
      URL: values.URL ?? "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const BannerImage =
      values?.BannerImage &&
      values?.BannerImage.length &&
      typeof values?.BannerImage !== "string"
        ? values.BannerImage[0].getRawFile()
        : null;
    const SliderImage =
      values?.SliderImage &&
      values?.SliderImage.length &&
      typeof values?.SliderImage !== "string"
        ? values.SliderImage[0].getRawFile()
        : null;
    const CardImage =
      values?.CardImage &&
      values?.CardImage.length &&
      typeof values?.CardImage !== "string"
        ? values.CardImage[0].getRawFile()
        : null;
    try {
      if (CaseStudiesID) {
        const editPayload = {
          ...formData,
          ID: CaseStudiesID,
        };

        const response = await dispatch(updateCaseStudies(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.BannerImage && typeof values?.BannerImage !== "string") ||
            (values?.CardImage && typeof values?.CardImage !== "string") ||
            (values?.SliderImage && typeof values?.SliderImage !== "string")
          ) {
            ImageData.append("CardImage", CardImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("SliderImage", SliderImage);
            ImageData.append("ID", CaseStudiesID);
            const imageResponse = await dispatch(
              CaseStudiesDetailsImageUpload(ImageData)
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/casestudies");
            } else {
              navigate("/casestudies");
            }
          } else {
            navigate("/casestudies");
          }
        }
      } else {
        const response = await dispatch(createCaseStudies(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.BannerImage && typeof values?.BannerImage !== "string") ||
            (values?.CardImage && typeof values?.CardImage !== "string") ||
            (values?.SliderImage &&
              typeof values?.SliderImage !== "string" &&
              response?.payload?.Data?.ID)
          ) {
            ImageData.append("CardImage", CardImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("SliderImage", SliderImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(
              CaseStudiesDetailsImageUpload(ImageData)
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/casestudies");
            } else {
              navigate("/casestudies");
            }
          } else {
            navigate("/casestudies");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      <div>
        {loading && <LoadingPanel gridRef={gridRef} />}
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={CaseStudiesDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {CaseStudiesID
                        ? "Update  Case Studie"
                        : "Create  Case Studie"}
                    </Typography.h4>
                  </GridLayoutItem>
                    <GridLayoutItem colSpan={1}>
                      <Field
                        name="Title"
                        label="Title"
                        placeholder="Title"
                        onChange={(e) => handleCreateSlug(e, formRenderProps)}
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={1}>
                      <Field
                        name="URL"
                        label="URL"
                        placeholder="URL"
                        astrike={true}
                        component={FormTextField}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={`IsActive`}
                        label="Status"
                        placeholder="i.e. Active"
                        component={FormSelectionField}
                        validator={requiredValidator}
                        astrike={true}
                        options={STATUSARRAY?.map((status: any) => {
                          return {
                            value: status?.value,
                            label: status?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Description"
                      label="Slider Detail"
                      placeholder="Slider Detail"
                      component={FormRichTextField}
                      minHeight={100}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="CaseStudieDetail"
                      label="CaseStudie Detail"
                      placeholder="CaseStudie Detail"
                      component={FormRichTextField}
                      minHeight={500}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Banner Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`SliderImage`}
                      label="Slider Image"
                      type="file"
                      accept="image/*"
                      alternateName="Slider Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`CardImage`}
                      label="Card Image"
                      type="file"
                      accept="image/*"
                      alternateName="Card Image"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={CaseStudiesID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/casestudies")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateCaseStudies;
